import { Avatar, Skeleton } from 'antd'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Helmet } from 'react-helmet'
import { isEmpty } from 'lodash-es'
import { loadingInitSelector, outletByCurrentUserSelector } from '@/redux/selectors'
import { setOutlet } from '@/redux/cashierSlice'
import { generateCardName } from '@/utils/helper'
import PushPin, { PIN_COLORS } from '@/components/UI/PushPin'
import OutletSelectorHeader from './OutletSelectorHeader'
import styles from './style.module.scss'

const OutletSelectorPage = () => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const [imageErrors, setImageErrors] = useState(() => new Set())

  const outlets = useSelector(outletByCurrentUserSelector)
  const loadingInit = useSelector(loadingInitSelector)

  const handleImageError = useCallback((outletId) => {
    setImageErrors((prev) => new Set(prev).add(outletId))
  }, [])

  const handleSelectOutlet = useCallback(
    (outlet) => {
      dispatch(setOutlet(outlet))
    },
    [dispatch],
  )

  return (
    <>
      <Helmet title={formatMessage({ id: 'kledoPos.outlet_placeholder' })} />
      <div className={styles.container}>
        <img
          src="resources/images/login-bg-decor.svg?v=3"
          alt=""
          aria-hidden="true"
          className={styles.bgDecor}
        />
        <div className={styles.contentContainer}>
          <OutletSelectorHeader
            message={formatMessage({ id: 'please_select_outlet_to_continue' })}
          />
          {loadingInit ? (
            <Skeleton active paragraph={{ rows: 4 }} className="my-4" />
          ) : (
            <div className={styles.outletGrid}>
              {outlets?.map((outlet, index) => (
                <div
                  key={outlet.id}
                  className={styles.outletCard}
                  onClick={() => handleSelectOutlet(outlet)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSelectOutlet(outlet)
                    }
                  }}
                >
                  <PushPin color={PIN_COLORS[index % PIN_COLORS.length]} className={styles.pin} />
                  <div className={styles.outletImageWrap}>
                    {!isEmpty(outlet.photo) && !imageErrors.has(outlet.id) ? (
                      <img
                        src={outlet.photo}
                        alt={outlet.name}
                        className={styles.outletImage}
                        onError={() => handleImageError(outlet.id)}
                      />
                    ) : (
                      <Avatar style={{ backgroundColor: 'rgba(70, 120, 243, 0.35)' }} size={64}>
                        <span
                          style={{
                            fontSize: 24,
                            fontWeight: 'bold',
                            color: 'white',
                            textTransform: 'uppercase',
                          }}
                        >
                          {generateCardName(outlet.name || '')}
                        </span>
                      </Avatar>
                    )}
                  </div>
                  <div className={styles.outletName}>{outlet.name}</div>
                  <span className={styles.rule} />
                </div>
              ))}
            </div>
          )}
          <div className={styles.footer}>
            Powered by <strong>Kledo</strong> · ©{` ${new Date().getFullYear()} Kledo`}
          </div>
        </div>
      </div>
    </>
  )
}

export default OutletSelectorPage
